// ============================================================
// StepPreviousAcademicsAndDocuments Component
// ============================================================

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import { Plus, Trash2, GraduationCap, FileText, CheckCircle2, Upload } from 'lucide-react';
import { tokens } from '@/theme/tokens';

const BOARDS = ['CBSE', 'ICSE', 'State Board', 'IB', 'IGCSE', 'Other'];
const MEDIUMS = ['English', 'Hindi', 'Regional', 'Other'];

export const STANDARD_DOCUMENT_TYPES = [
  { key: 'TRANSFER_CERTIFICATE', label: 'Transfer Certificate (TC)' },
  { key: 'BIRTH_CERTIFICATE', label: 'Birth Certificate' },
  { key: 'AADHAAR', label: 'Aadhaar Card / ID Proof' },
  { key: 'MARKSHEET', label: 'Previous Year Marksheet' },
  { key: 'CASTE_CERTIFICATE', label: 'Caste Certificate' },
  { key: 'MIGRATION_CERTIFICATE', label: 'Migration Certificate' },
  { key: 'CHARACTER_CERTIFICATE', label: 'Character Certificate' },
  { key: 'PASSPORT', label: 'Passport / Visa Proof' },
];

export const normalizeDocTypeKey = (str) => {
  if (!str) return '';
  const s = String(str).toUpperCase();
  if (s.includes('AADHAAR') || s.includes('ADHAAR') || s.includes('ID_PROOF') || s.includes('ID PROOF')) return 'AADHAAR';
  if (s.includes('TRANSFER') || s.includes('TC')) return 'TRANSFER_CERTIFICATE';
  if (s.includes('BIRTH')) return 'BIRTH_CERTIFICATE';
  if (s.includes('MARKSHEET') || s.includes('MARK_SHEET')) return 'MARKSHEET';
  if (s.includes('CASTE')) return 'CASTE_CERTIFICATE';
  if (s.includes('MIGRATION')) return 'MIGRATION_CERTIFICATE';
  if (s.includes('CHARACTER')) return 'CHARACTER_CERTIFICATE';
  if (s.includes('PASSPORT') || s.includes('VISA')) return 'PASSPORT';
  return s.replace(/[^A-Z0-9]/g, '_').replace(/_+/g, '_');
};

export const getPrettyDocLabel = (typeStr) => {
  const norm = normalizeDocTypeKey(typeStr);
  const found = STANDARD_DOCUMENT_TYPES.find((d) => d.key === norm);
  if (found) return found.label;
  return String(typeStr || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

export function StepPreviousAcademicsAndDocuments({ formik, isEditMode = false }) {
  const { values, setFieldValue, handleChange, handleBlur } = formik;
  const [customDocType, setCustomDocType] = useState('');

  const previousAcademics = Array.isArray(values.previous_academics) ? values.previous_academics : [];
  const documents = Array.isArray(values.documents) ? values.documents : [];

  // Add a new previous academic record
  const handleAddAcademic = () => {
    const newRecord = {
      school_name: '',
      board: 'CBSE',
      medium: 'English',
      last_class: '',
      percentage: '',
      tc_number: '',
      tc_date: '',
      remarks: '',
    };
    setFieldValue('previous_academics', [...previousAcademics, newRecord]);
  };

  // Remove a previous academic record
  const handleRemoveAcademic = (index) => {
    const updated = previousAcademics.filter((_, i) => i !== index);
    setFieldValue('previous_academics', updated);
  };

  // Check if document type is already in list
  const isDocSelected = (docType) => {
    const targetNorm = normalizeDocTypeKey(docType);
    return documents.some((doc) => {
      const typeStr = typeof doc === 'string' ? doc : doc?.document_type || '';
      return normalizeDocTypeKey(typeStr) === targetNorm;
    });
  };

  // Toggle document type
  const handleToggleDoc = (docType) => {
    const targetNorm = normalizeDocTypeKey(docType);
    if (isDocSelected(docType)) {
      const updated = documents.filter((doc) => {
        const typeStr = typeof doc === 'string' ? doc : doc?.document_type || '';
        return normalizeDocTypeKey(typeStr) !== targetNorm;
      });
      setFieldValue('documents', updated);
    } else {
      setFieldValue('documents', [...documents, { document_type: targetNorm, document_number: '', file: null }]);
    }
  };

  // Add custom document type
  const handleAddCustomDoc = () => {
    if (!customDocType || !customDocType.trim()) return;
    const trimmed = customDocType.trim();
    if (!isDocSelected(trimmed)) {
      setFieldValue('documents', [...documents, { document_type: trimmed, document_number: '', file: null }]);
    }
    setCustomDocType('');
  };

  const handleDocumentNumberChange = (index, val) => {
    const updated = documents.map((doc, idx) => {
      if (idx !== index) return doc;
      const baseObj = typeof doc === 'string' ? { document_type: doc } : { ...doc };
      return { ...baseObj, document_number: val };
    });
    setFieldValue('documents', updated);
  };

  const handleDocumentFileChange = (index, fileObj) => {
    const updated = documents.map((doc, idx) => {
      if (idx !== index) return doc;
      const baseObj = typeof doc === 'string' ? { document_type: doc } : { ...doc };
      return { ...baseObj, file: fileObj };
    });
    setFieldValue('documents', updated);
  };

  return (
    <Box sx={{ py: 1 }}>
      {/* Previous Academics Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: tokens.radius.md,
                backgroundColor: 'primary.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.main',
              }}
            >
              <GraduationCap size={20} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                Previous Academic Records
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Add information about the student&apos;s previous schooling, transfer certificates, and grades
              </Typography>
            </Box>
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={handleAddAcademic}
            sx={{ borderRadius: tokens.radius.full }}
          >
            Add Academic Record
          </Button>
        </Box>

        {previousAcademics.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              borderRadius: tokens.radius.md,
              bgcolor: 'background.default',
              borderStyle: 'dashed',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              No previous academic records added for this student.
            </Typography>
            <Button
              variant="text"
              size="small"
              startIcon={<Plus size={16} />}
              onClick={handleAddAcademic}
              sx={{ fontWeight: 600 }}
            >
              Add Record Now
            </Button>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {previousAcademics.map((item, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: tokens.radius.md,
                  position: 'relative',
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                    pb: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                    Record #{index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveAcademic(index)}
                    title="Remove Record"
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Previous School Name"
                      name={`previous_academics.${index}.school_name`}
                      value={item.school_name || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. St. Xavier's High School"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      select
                      fullWidth
                      label="Board"
                      name={`previous_academics.${index}.board`}
                      value={item.board || 'CBSE'}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      {BOARDS.map((board) => (
                        <MenuItem key={board} value={board}>
                          {board}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      select
                      fullWidth
                      label="Medium"
                      name={`previous_academics.${index}.medium`}
                      value={item.medium || 'English'}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      {MEDIUMS.map((med) => (
                        <MenuItem key={med} value={med}>
                          {med}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="Last Class Attended"
                      name={`previous_academics.${index}.last_class`}
                      value={item.last_class || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. Class VIII"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Percentage / Marks (%)"
                      name={`previous_academics.${index}.percentage`}
                      value={item.percentage ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. 85.5"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="Transfer Certificate (TC) Number"
                      name={`previous_academics.${index}.tc_number`}
                      value={item.tc_number || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. TC-2026-989"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="date"
                      label="TC Date"
                      name={`previous_academics.${index}.tc_date`}
                      value={item.tc_date || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Remarks / Notes"
                      name={`previous_academics.${index}.remarks`}
                      value={item.remarks || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Any additional remarks"
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Submitted Documents Section */}
      {isEditMode && (
        <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: tokens.radius.md,
              backgroundColor: 'success.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'success.main',
            }}
          >
            <FileText size={20} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              Submitted Documents
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Select or add the documents submitted by the student at the time of admission
            </Typography>
          </Box>
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            borderRadius: tokens.radius.md,
            bgcolor: 'background.default',
            mb: 2,
          }}
        >
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            STANDARD DOCUMENTS LIST (CLICK TO TOGGLE)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {STANDARD_DOCUMENT_TYPES.map((docItem) => {
              const selected = isDocSelected(docItem.key);
              return (
                <Chip
                  key={docItem.key}
                  label={docItem.label}
                  onClick={() => handleToggleDoc(docItem.key)}
                  icon={selected ? <CheckCircle2 size={15} /> : undefined}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: selected ? 600 : 500,
                    borderRadius: tokens.radius.md,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                />
              );
            })}
          </Box>

          <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              ADD OTHER / CUSTOM DOCUMENT TYPE
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, maxWidth: 500 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Income Certificate / Domicile Certificate"
                value={customDocType}
                onChange={(e) => setCustomDocType(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCustomDoc();
                  }
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleAddCustomDoc}
                disabled={!customDocType.trim()}
                sx={{ px: 3, borderRadius: tokens.radius.sm, whiteSpace: 'nowrap' }}
              >
                Add Document
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Selected Documents Details & Files */}
        {documents.length > 0 && (
          <Box>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              SELECTED FOR ADMISSION ({documents.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {documents.map((doc, idx) => {
                const rawType = typeof doc === 'string' ? doc : doc?.document_type || 'Document';
                const label = getPrettyDocLabel(rawType);
                const docNum = typeof doc === 'string' ? '' : doc?.document_number || '';
                const attachedFile = typeof doc === 'object' ? doc?.file : null;
                const fileUrl = typeof doc === 'object' ? doc?.file_url : null;

                return (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: tokens.radius.md,
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ minWidth: 200, flex: 1 }}>
                      <Chip
                        label={label}
                        onDelete={() => handleToggleDoc(rawType)}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600, borderRadius: tokens.radius.sm }}
                      />
                    </Box>

                    <Box sx={{ minWidth: 220, flex: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Document Number (Optional)"
                        placeholder="e.g. BC123456 / 123456789012"
                        value={docNum}
                        onChange={(e) => handleDocumentNumberChange(idx, e.target.value)}
                      />
                    </Box>

                    <Box sx={{ minWidth: 240, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        component="label"
                        startIcon={<Upload size={16} />}
                        sx={{ borderRadius: tokens.radius.sm, whiteSpace: 'nowrap' }}
                      >
                        {attachedFile ? 'Change File' : fileUrl ? 'Replace File' : 'Attach File'}
                        <input
                          type="file"
                          hidden
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              handleDocumentFileChange(idx, e.target.files[0]);
                            }
                          }}
                        />
                      </Button>
                      {attachedFile ? (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 140 }}>
                          {attachedFile.name}
                        </Typography>
                      ) : fileUrl ? (
                        <Button
                          variant="text"
                          size="small"
                          component="a"
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<FileText size={14} />}
                          sx={{ fontSize: 12, textTransform: 'none', fontWeight: 600 }}
                        >
                          View Uploaded
                        </Button>
                      ) : null}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
      )}
    </Box>
  );
}
